import type { OnboardingAnswers, OnboardingData } from '~/types/onboarding';
import { buildPreferencesIdentifyTraits } from '~/utils/ninetailed/buildPreferencesIdentifyTraits';

export type IdentifyCustomerPreferencesOptions = {
  identify?: (
    userId: string,
    traits?: Record<string, unknown>
  ) => Promise<unknown>;
  userId?: string | null;
  answers: OnboardingAnswers;
  onboardingData: OnboardingData | null | undefined;
  baseTraits?: Record<string, unknown>;
  onError?: (error: Error) => void;
};

/**
 * Sends customer preferences as Ninetailed identify traits.
 * No-ops when the SDK or authenticated user id is missing.
 */
export function identifyCustomerPreferences(
  options: IdentifyCustomerPreferencesOptions
): void {
  const {
    identify,
    userId,
    answers,
    onboardingData,
    baseTraits = {},
    onError
  } = options;

  if (!identify || !userId) {
    return;
  }

  const normalizeError = (error: unknown): Error =>
    error instanceof Error
      ? error
      : new Error('Failed to identify customer preferences in Ninetailed');

  // identify may throw sync or return a Promise; both must hit onError
  void (async () => {
    try {
      await identify(userId, {
        ...baseTraits,
        ...buildPreferencesIdentifyTraits(answers, onboardingData)
      });
    } catch (error: unknown) {
      onError?.(normalizeError(error));
    }
  })();
}
